import React from "react";
import Layer from "../../Layer";
import PageHeader from "../../Component/PageHeader";
import CardMyAccountsResult from "../../Component/Card/Report/CardMyAccountsResult";
import CardReportSettings from "../../Component/Card/Report/CardReportSettings";
import moment from "moment";
import api from "../../Seating/Security/api";
import User from "../../Utility/Crud/User";
import Setting from "../../Utility/Setting";
import MUIVisibilityIcon from "@mui/icons-material/Visibility";
import MUIVisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import MUIButton from "@mui/material/Button";
import MUIAddIcon from "@mui/icons-material/Add";
import MainContext from "../../Context/MainContext/MainContext";
import Region from "../../Utility/Crud/Region";
import Location from "../../Utility/Crud/Location";
import Security from "../../Utility/Security";
import WidgetDashboardRow from "../../Component/WidgetDashboardRow";
import Dashboard from "../../Utility/Crud/Dashboard";

export default class MyAccounts extends Layer {
  constructor(props) {
    super(props);
    this.state.activeWidget = props.activeWidget;
    this.state.loadingWidgetConfigurationFromDashboard = true;
    this.state.dashboardSettingsComponent = {};
    this.state.dashboardConfiguration = {};
    Setting.set(`${this.getDetailKey()}.settings`, {
      ...Setting.get(`${this.getDetailKey()}.settings`),
      onlyActiveAccounts: {
        activeAccounts:
          Setting.get(`${this.getDetailKey()}.settings`)?.onlyActiveAccounts
            ?.activeAccounts ?? true,
      },
    });
  }

  static contextType = MainContext;

  afterComponentDidMount() {
    this.setState({
      showInactiveUsers: false,
      selectedAccounts: {},
    });

    this.waitForContext().then(() => {
      const viewAllForOrganization = Security.hasPermission(
        "myAccounts.viewAllForOrganization",
      );
      const viewAllForLocation = Security.hasPermission(
        "myAccounts.viewAllForLocation",
      );

      if (viewAllForOrganization) {
        this.setState({
          userRolesToFilter: [],
          restrictToCurrentUser: false,
          restrictToCurrentUserLocations: false,
        });
      } else if (viewAllForLocation) {
        this.setState({
          userRolesToFilter: ["SALES", "SALES_MANAGER"],
          restrictToCurrentUser: false,
          restrictToCurrentUserLocations: true,
        });
      } else {
        this.setState({
          userRolesToFilter: ["SALES", "SALES_MANAGER"],
          restrictToCurrentUser: true,
          restrictToCurrentUserLocations: true,
        });
      }

      if (Dashboard.isDashboardRoute()) {
        return;
      }

      const settings = Setting.get(`${this.getDetailKey()}.settings`);
      if (!Setting.get(`${this.getDetailKey()}.autoRunReport`)) {
        return;
      }

      this.getAccountPerformance(settings);
    });
  }

  componentDidUpdate(prevProps) {
    if (Dashboard.isDashboardRoute()) {
      if (prevProps.widgetConfiguration !== this.props.widgetConfiguration) {
        this.getAccountPerformance(
          this.props.widgetConfiguration.dashboardConfiguration,
        );
        this.setState({
          dashboardConfiguration:
            this.props.widgetConfiguration.dashboardConfiguration,
        });
      }
    }
  }

  renderHeader() {
    if (Dashboard.isDashboardRoute()) {
      return null;
    }

    const buttons = [
      <MUIButton
        loading={this.state.isProcessing}
        loadingPosition="start"
        onClick={() => {
          window.open("/account/create", "_blank");
        }}
        variant="outlined"
        startIcon={<MUIAddIcon />}
      >
        Create New
      </MUIButton>,
    ];

    return <PageHeader title={this.getTitle()} buttons={buttons} />;
  }

  getTitle() {
    return "My Accounts";
  }

  getMaxWidth() {
    if (Dashboard.isDashboardRoute()) {
      return super.getMaxWidth();
    }
    return 1400;
  }

  /**
   * Returns a camel-cased string to use as a unique identifier
   * for the subclass, based on its `getTitle()` implementation.
   */
  getDetailKey() {
    const header = this.getTitle();
    if (!header) return "myAccounts";

    const noSpaces = header
      .replace(/[^a-zA-Z0-9]+/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join("");

    const lowercaseFirstLetter =
      noSpaces.charAt(0).toLowerCase() + noSpaces.slice(1);
    return `reports.${lowercaseFirstLetter}`;
  }

  handleSelectedAccountsChange(selectedAccounts) {
    this.setState({ selectedAccounts: selectedAccounts });
  }
  /**
   * Sets new grouping in state and local storage.
   */
  handleGroupingChange(grouping) {
    this.setState({
      grouping: grouping,
    });

    Setting.set(`${this.getDetailKey()}.grouping`, grouping);
  }

  /**
   * Sets new sorting in state and local storage.
   */
  handleSortingChange(sorting) {
    this.setState({
      sorting: sorting,
    });

    Setting.set(`${this.getDetailKey()}.sorting`, sorting);
  }

  /**
   * Filters based on PCR links (roles seem too inconsistent to accurately use)
   * If no links act as normal restrict to current user and disable.
   * @param {*} users
   * @returns
   */
  getFilterUsersIdsOnRestriction() {
    const currentUser = this.context.currentUser;
    const userIds = [];
    // always push current user id
    userIds.push(currentUser.id);
    // find any links if they exist
    Object.values(this.context.salesPcrLinksIndexed)
      .map((link) => {
        if (link.patientCareRepId === currentUser.id) {
          userIds.push(link.salesRepId);
        }
      })
      .filter((x) => x);
    // return available userIds.
    return userIds;
  }

  /**
   * Filters usersLocations
   * @returns locationsIds
   */
  getAccessibleLocationsIds() {
    const currentUser = this.context.currentUser;
    const locationsForScope = new Set(
      User.getFilteredLocations(
        currentUser,
        this.context.userprofileLocationsIndexed,
        this.context.locationsIndexed,
      ).map((location) => location.id),
    );
    const userIds = [];
    if (this.state.restrictToCurrentUserLocations) {
      Object.values(this.context.salesPcrLinksIndexed)
        .map((link) => {
          if (link.patientCareRepId === currentUser.id) {
            userIds.push(link.salesRepId);
          }
        })
        .filter((x) => x);

      userIds.forEach((userId) => {
        User.getFilteredLocations(
          this.context.usersIndexed[userId],
          this.context.userprofileLocationsIndexed,
          this.context.locationsIndexed,
        ).forEach((location) => locationsForScope.add(location.id));
      });
    } else {
      return [];
    }
    return Array.from(locationsForScope);
  }

  /**
   * Returns the list of accessible regionsIds for the given region type.
   * This encapsulates the logic for user-based filtering, sorting, and type filtering.
   */
  getAccessibleRegionsIds() {
    const regionsForScope = this.state.restrictToCurrentUserLocations
      ? (() => {
          const userLocations = this.getAccessibleLocationsIds();
          const currentUserRegions = new Set(
            userLocations
              .map(
                (location) =>
                  this.context.regionsIndexed[
                    this.context.locationsIndexed[location]?.regionId
                  ],
              )
              .filter((region) => region),
          );
          return Array.from(currentUserRegions);
        })()
      : [];
    return regionsForScope
      .filter((region) => region.regionTypeTermId === 11) // SALES REGION
      .map((region) => region.id);
  }

  getGroupSummaryItems() {
    return [
      {
        columnName: "points",
        type: "sum",
        showInGroupFooter: false,
        alignByColumn: true,
      },
    ];
  }

  waitForContext() {
    return new Promise((resolve) => {
      const check = () => {
        if (
          this.context?.loaded === true &&
          this.context?.usersIndexed &&
          this.context?.regionsIndexed &&
          this.context?.locationsIndexed &&
          this.context?.currentUser
        ) {
          resolve();
          return;
        }
        setTimeout(check, 50);
      };
      check();
    });
  }

  runReportAfterUpdate(trigger) {
    if (trigger) {
      this.getAccountPerformance(
        Setting.get(`${this.getDetailKey()}.settings`),
      );
    }
  }
  /**
   * Groups items by region/location, builds columns,
   * and prepares the rows for rendering.
   *
   * @param {object} settings - Optional report settings containing dateRange.- End date filter.
   * @
   */
  async getAccountPerformance(settings) {
    this.setState({ loading: true });
    const beginDate = settings?.dateRange?.beginDate;
    const endDate = settings?.dateRange?.endDate;
    let regionIds = settings?.regionLocation?.regionIds || [];
    let locationIds = settings?.regionLocation?.locationIds || [];
    const isTargetAccount = settings?.accountStatus?.status === "target";
    const isTopAccount = settings?.accountStatus?.status === "top";
    const activeAccounts = settings.onlyActiveAccounts.activeAccounts;
    let userIds = settings?.user?.userprofileIds || [];

    if (locationIds.length === 0 && this.state.restrictToCurrentUserLocations) {
      locationIds = this.getAccessibleLocationsIds();
    }
    if (regionIds.length === 0 && this.state.restrictToCurrentUserLocations) {
      regionIds = this.getAccessibleRegionsIds();
    }

    if (userIds.length === 0 && this.state.restrictToCurrentUser) {
      userIds = this.getFilterUsersIdsOnRestriction();
    }

    await api
      .send("POST", "/reports/accountPerformanceReport", {
        beginDate: beginDate,
        endDate: endDate,
        regionIds: regionIds,
        locationIds: locationIds,
        isTargetAccount: isTargetAccount,
        activeAccounts: activeAccounts,
        userIds: userIds,
        isTopAccount: isTopAccount,
      })
      .then((response) => {
        const multipleLocations = locationIds.length === 1 ? false : true;
        const columns = this.buildColumns(multipleLocations);
        const defaultColumnWidths = this.buildColumnWidths(columns);
        const rows = this.buildRows(response);

        const exportFilename = `${this.getTitle()} - ${moment(this.loadedAt).format("YYYY-MM-DD h mm a")}.csv`;
        this.setState({
          rows: rows,
          loading: false,
          defaultColumnWidths: defaultColumnWidths,
          columns: columns,
          loadedAt: new Date(),
          exportFilename: exportFilename,
          selectedAccounts: {},
        });
      })
      .catch((error) => {
        this.setState({ loading: false, loadedAt: new Date() });
        console.error("Accounts Report failed", error);
      })
      .finally(() => {
        Setting.set(`${this.getDetailKey()}.autoRunReport`, false);
      });
  }

  /**
   * Builds the column definitions for the scorecard table.
   * @returns {Array<object>} Array of column definitions.
   */
  buildColumns(multipleLocations) {
    return [
      {
        title: " ",
        name: "checkbox",
        type: "checkbox",
        align: "center",
        width: 80,
      },
      { title: "Account", name: "account", type: "string", width: 200 },
      { title: "Type", name: "accountTypeId", type: "string", width: 90 },
      ...(multipleLocations
        ? [{ title: "Location", name: "location", type: "string", width: 100 }]
        : []),
      {
        title: "Sales Rep",
        name: "salesRepresentative",
        type: "string",
        width: 130,
      },
      { title: "Last IS", name: "lastInServiceDate", type: "date", width: 90 },
      {
        title: "Days IS",
        name: "daysSinceLastInService",
        type: "number",
        align: "right",
        width: 90,
      },
      {
        title: "Last Call",
        name: "lastCalledOnDate",
        type: "date",
        width: 100,
      },
      {
        title: "# IS",
        name: "inServices",
        type: "number",
        align: "right",
        width: 70,
      },
      {
        title: "# Calls",
        name: "calls",
        type: "number",
        align: "right",
        width: 90,
      },
      {
        title: "# Patients",
        name: "patients",
        type: "number",
        align: "right",
        width: 120,
      },
      {
        title: "Points",
        name: "points",
        type: "points",
        align: "right",
        width: 100,
      },
      {
        title: "Points Year",
        name: "pointsYear",
        type: "points",
        align: "right",
        width: 120,
      },
      { title: "Status", name: "status", type: "string", width: 90 },
      { title: "Won?", name: "won", type: "booleanCheck", width: 90 },
    ];
  }

  buildColumnWidths(columns) {
    return columns.map((col) => ({
      columnName: col.name,
      width: col.width || 160,
    }));
  }

  /**
   * Builds rows from raw API data.
   * @param {object} data - The raw data from the API.
   * @returns {object} rows - The formatted rows for the report.
   */
  buildRows(data) {
    const rows = [];
    for (const row of Object.values(data.accountResult)) {
      rows.push({
        id: row["id"],
        account: row["name"] || "",
        accountTypeId: row["account_type_id"]
          ? this.context.accountTypesIndexed[row["account_type_id"]]?.name
          : "",
        region: row["region_id"]
          ? Region.getDisplayName(this.context.regionsIndexed[row["region_id"]])
          : "",
        location: row["location_id"]
          ? Location.getDisplayName(
              this.context.locationsIndexed[row["location_id"]],
            )
          : "",
        salesRepresentative: row["owner_id"]
          ? User.getDisplayName(this.context.usersIndexed[row["owner_id"]])
          : "",
        lastInServiceDate: row["last_in_service_date"],
        lastCalledOnDate: row["last_call_date"],
        inServices: row["in_service_count"] || 0,
        calls: row["call_count"] || 0,
        patients: row["patient_count"] || 0,
        points: row["sum_points"] || 0,
        pointsYear: row["delivered_points_sum_past_year"] || 0,
        won: row["sum_points"] > 10 && row["is_target"],
        status:
          row["delivered_points_sum_past_year"] >= 10 && row["is_target"]
            ? "Target/Top"
            : row["delivered_points_sum_past_year"] >= 10
              ? "Top"
              : row["is_target"]
                ? "Target"
                : "",
        daysSinceLastInService: row["last_in_service_date"]
          ? moment().diff(moment(row["last_in_service_date"]), "days")
          : null,
      });
    }
    return rows;
  }

  /**
   * Renders the results card with all scorecard data.
   * @returns {JSX.Element} CardAccountPerformanceResults component.
   */
  renderContent() {
    return (
      <React.Fragment>
        {Dashboard.isDashboardRoute() ? (
          <>
            <WidgetDashboardRow
              activeWidget={this.props.activeWidget}
              dashboardWidgets={this.props.dashboardWidgets}
              onActiveWidgetChange={this.props.onActiveWidgetChange}
              updateActiveWidgetConfiguration={
                this.props.updateActiveWidgetConfiguration
              }
            />
            <CardReportSettings
              settings={this.state.dashboardConfiguration}
              hideTitle={true}
              isLoading={this.state.loading}
              initiallyCollapsed={true}
              settingComponents={{
                dateRange: {},
                regionLocation: {
                  regionId: 11, // 11 === Sales Region Id
                  restrictToCurrentUserLocations:
                    this.state.restrictToCurrentUserLocations,
                },
                accountStatus: {},
                onlyActiveAccounts: {},
                user: {
                  userRolesToFilter: this.state.userRolesToFilter,
                  restrictToCurrentUser: this.state.restrictToCurrentUser,
                  showInactiveUsers: this.state.showInactiveUsers,
                },
              }}
            />
          </>
        ) : (
          <CardReportSettings
            onRun={(settings) => this.getAccountPerformance(settings)}
            isLoading={this.state.loading}
            initiallyCollapsed={
              Setting.get(`${this.getDetailKey()}.autoRunReport`) || false
            }
            menu={true}
            menuActions={[
              {
                label: this.state.showInactiveUsers
                  ? "Hide Inactive Users"
                  : "Show Inactive Users",
                icon: this.state.showInactiveUsers ? (
                  <MUIVisibilityOffIcon />
                ) : (
                  <MUIVisibilityIcon />
                ),
                onClick: () => {
                  this.setState({
                    showInactiveUsers: !this.state.showInactiveUsers,
                  });
                },
              },
            ]}
            settingComponents={{
              dateRange: {
                validationRules: {
                  beginDate: {
                    required: true,
                  },
                  endDate: {
                    required: true,
                  },
                },
                hint: "Calculates points and totals within the specified date range. If filtering to active accounts, includes accounts active within date range.",
              },
              // 11 === Sales Region Id
              regionLocation: {
                regionId: 11,
                restrictToCurrentUserLocations:
                  this.state.restrictToCurrentUserLocations,
              },
              accountStatus: {
                hint: "Target Account Includes only accounts marked as target accounts. Top Accounts include accounts who points are greater than ten within the past year.",
              },
              onlyActiveAccounts: {
                hint: "Includes only accounts that have had an appointment, call, new order setup, or accounts that are targeted within the selected date range.",
              },
              user: {
                userRolesToFilter: this.state.userRolesToFilter,
                restrictToCurrentUser: this.state.restrictToCurrentUser,
                showInactiveUsers: this.state.showInactiveUsers,
              },
            }}
          />
        )}
        <CardMyAccountsResult
          rows={this.state.rows}
          columns={this.state.columns}
          defaultColumnWidths={this.state.defaultColumnWidths}
          sorting={this.state.sorting}
          onSortingChange={this.handleSortingChange.bind(this)}
          grouping={this.state.grouping}
          onGroupingChange={this.handleGroupingChange.bind(this)}
          groupSummaryItems={this.getGroupSummaryItems()}
          selectedAccounts={this.state.selectedAccounts}
          selectedAccountsChange={this.handleSelectedAccountsChange.bind(this)}
          isLoading={this.state.loading}
          loadedAt={this.state.loadedAt}
          title={this.getTitle()}
          runReportAfterUpdate={this.runReportAfterUpdate.bind(this)}
          exportFilename={this.state.exportFilename}
        />
      </React.Fragment>
    );
  }
}
